import { ImageUploader } from "modules/uploader";
import { useI18n } from "i18n";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Delete as DeleteIcon } from "@remo-co/ui-core/src/icons/Delete";
import useCompany from "modules/company/hooks/useCompany";
import { useCallback, useContext, useEffect, useState } from "react";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import {
  deleteWhiteLabelSignInBackgroundURL,
  updateCompanySettings,
} from "services/apiService/apis";
import { ICompanySettings } from "modules/company/types";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import ImagePlaceHolder from "modules/uploader/ImagePlaceHolder";
import UploadCaption, {
  BestUploadSizes,
  MaxUploadSizes,
} from "modules/uploader/UploadCaption";
import { selectWhiteLabelSignInBackgroundURL } from "modules/companySettings/redux/selectors";
import { useSelector } from "react-redux";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { Button } from "@remo-co/ui-core/src/components/Button";
import * as FileStoragePaths from "../../../../services/firebaseService/storagePaths";
import { useStyles } from "./styles";

interface Props {
  enabledAgencyWhiteLabelling: boolean;
}

export const SignInImageBackground = ({
  enabledAgencyWhiteLabelling,
}: Props) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { setCompanySettings, resetCompanySettings } =
    useCompanySettingsActions();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { company } = useCompany();
  const savedWhiteLabelSignInBackgroundURL = useSelector(
    selectWhiteLabelSignInBackgroundURL,
  );
  const [whiteLabelSignInBackgroundURL, setWhiteLabelSignInBackgroundURL] =
    useState({
      error: false,
      value: "",
      message: "",
      touched: false,
    });
  const { t } = useI18n(["common", "settings"]);
  const styles = useStyles();
  const buttonText = whiteLabelSignInBackgroundURL.value
    ? t("button.change.image")
    : t("button.upload.new.image");

  const onLogoFieldChange = (imageURL: string) => {
    setWhiteLabelSignInBackgroundURL({
      value: imageURL,
      error: false,
      message: "",
      touched: true,
    });
  };

  useEffect(() => {
    if (
      savedWhiteLabelSignInBackgroundURL &&
      !whiteLabelSignInBackgroundURL.value
    ) {
      setWhiteLabelSignInBackgroundURL({
        ...whiteLabelSignInBackgroundURL,
        value: savedWhiteLabelSignInBackgroundURL,
        touched: false,
      });
    }
  }, [whiteLabelSignInBackgroundURL, savedWhiteLabelSignInBackgroundURL]);

  const saveChanges = useCallback(
    async (companySettings: Partial<ICompanySettings>) => {
      if (company && company.id) {
        const res = await updateCompanySettings(company.id, {
          ...companySettings,
          company: company.id,
        });

        if (!res.isSuccess) {
          addErrorNotification({
            message: t("settings:agency.white.label.sign.in.background.error"),
          });

          return;
        }

        if (res.companySettings) {
          setCompanySettings(res.companySettings);
        }
        track(Events.AGENCY_WHITE_LABEL_SIGN_IN_BACKGROUND_CHANGED, {
          companyIds: company.id,
        });
        addSuccessNotification({
          message: t("settings:agency.white.label.sign.in.background.success"),
        });
      }
    },
    [
      addErrorNotification,
      addSuccessNotification,
      company,
      setCompanySettings,
      t,
      track,
    ],
  );

  const deleteSignInBackgroundURL = useCallback(async () => {
    if (company && company.id) {
      const res = await deleteWhiteLabelSignInBackgroundURL(company.id);

      if (!res.isSuccess) {
        addErrorNotification({
          message: t("settings:agency.white.label.sign.in.background.error"),
        });

        return;
      }

      if (res.companySettings) {
        resetCompanySettings();
        setCompanySettings(res.companySettings);
        setWhiteLabelSignInBackgroundURL({
          ...whiteLabelSignInBackgroundURL,
          value: "",
          touched: false,
        });
      }
      track(Events.AGENCY_WHITE_LABEL_SIGN_IN_BACKGROUND_REMOVED, {
        companyIds: company.id,
      });
      addSuccessNotification({
        message: t("settings:agency.white.label.sign.in.background.success"),
      });
    }
  }, [
    company,
    track,
    addSuccessNotification,
    t,
    addErrorNotification,
    resetCompanySettings,
    setCompanySettings,
    whiteLabelSignInBackgroundURL,
  ]);

  useEffect(() => {
    if (whiteLabelSignInBackgroundURL.touched) {
      saveChanges({
        whiteLabelSignInBackgroundURL: whiteLabelSignInBackgroundURL.value,
      });
      setWhiteLabelSignInBackgroundURL({
        ...whiteLabelSignInBackgroundURL,
        touched: false,
      });
    }
  }, [whiteLabelSignInBackgroundURL, saveChanges]);

  return (
    <Box className={styles.container}>
      <Typography variant="body1">
        {t("settings:sign.in.background.label")}
      </Typography>
      <div className={styles.imageUploadContainer}>
        <div className={styles.imageForm}>
          <div className={styles.companyImage}>
            {whiteLabelSignInBackgroundURL.value ? (
              <img
                src={whiteLabelSignInBackgroundURL.value}
                alt="Sign-in/sign-up page background"
                className={styles.image}
              />
            ) : (
              <ImagePlaceHolder />
            )}
          </div>
          <div className={styles.imageDetails}>
            <ImageUploader
              name="whitelabelSignInBackground"
              level={FileStoragePaths.UPLOAD_LEVEL.COMPANY}
              path={FileStoragePaths.AGENCY_WHITE_LABEL_SIGN_IN_BACKGROUND}
              maxFileSize="3MB"
              acceptedFileTypes={["image/x-icon", "image/jpeg", "image/png"]}
              onImageUploaded={onLogoFieldChange}
              disabled={!enabledAgencyWhiteLabelling}
              variant="secondary"
              color="blue"
              size="md"
              data-testid="change-sign-in-background-button"
            >
              {buttonText}
            </ImageUploader>
            {whiteLabelSignInBackgroundURL.value && (
              <Button
                isIconButton
                variant="secondary"
                color="dark"
                size="md"
                onClick={deleteSignInBackgroundURL}
                data-testid="whitelabel-sign-in-background-delete"
              >
                <DeleteIcon />
              </Button>
            )}
          </div>
        </div>
        <UploadCaption
          bestSize={BestUploadSizes.landscape}
          maxSize={MaxUploadSizes.biggest}
        />
      </div>
    </Box>
  );
};
